<!-- ApiPerformanceResults.vue -->
<script setup lang="ts">
import { reactive, computed, onMounted } from 'vue';
import { ActionButtonStyle } from '../../../../lib/Types/Types';
import { DegradedRequestProcessedModel } from '../../../../lib/Api/Performance';
import { ICol, RenderModeType, IUniqueKeyId, addUniqueKeyId } from '../../../../lib/ChoiceTableHelpers';
import { DisplayStyle, TextDisplayStyle } from '../../../../Enums/UiEnums';
import { NoTzDate } from '../../../../lib/DateUtils';
import { ChoiceApi, DateUtils } from '../../../../LibMain';
import SectionPanel from '../../../../components/SectionPanel.vue'
import MessageText from '../../../../components/Messages/MessageText.vue';
import ActionButtonV2 from '../../../../components/ActionButtonV2.vue';
import ChoiceButton from '../../../../components/Buttons/ChoiceButton.vue';
import DateTimePickerNoTZ from '../../../../components/Inputs/DateTimePickerNoTZ.vue';
import IntegerInput from '../../../../components/IntegerInput.vue';
import FoldingPanel from '../../../../components/FoldingPanel.vue';
import { isBangUser } from '../../../../lib/ChoiceUtils';
import ChoiceTable from '../../../../components/ChoiceTable.vue';
import MessageBadge from '../../../../components/Messages/MessageBadge.vue';
import Checkbox from '../../../../components/Inputs/Checkbox.vue';

    const props = defineProps<{
        resultsUserId?: string
    }>();

    const emit = defineEmits<{
        newDataLoaded: []
    }>();    

    defineExpose({clearData});

    const compData = reactive<{
        isBangUser?: boolean,

        startDateTime?: NoTzDate,
        minutesOfData: number,
        apiPerformanceResults?: (DegradedRequestProcessedModel & IUniqueKeyId)[],
        showCustomDataRangeInputs?: boolean,
        showBody?: boolean,
        showCallHandler?: boolean,
        showScreenName?: boolean,
        showScreenMethod?: boolean
    }>({
            minutesOfData: 10
    });

    const degradedApiColumns: ICol[] = [
        { Name: "Start Time", DataField: "startTime", RenderMode: RenderModeType.DateTime },
        { Name: "Send (ms)", DataField: "openToSendTimeMS" },
        { Name: "Complete (ms)", DataField: "openToCompletedMS" },
        { Name: "Method", DataField: "method" },
        { Name: "Http Status", DataField: "httpStatus" },
        { Name: "Endpoint", DataField: "endpointPath" }
    ];    

    onMounted(() =>{

        if (isBangUser())
        {
            compData.isBangUser = isBangUser();
        }
    });

    async function fetchTelemetryData(startDateTime: Date, minutesOfData: number)
    {
        if (!startDateTime)
        {
            alert("Invalid startDateTime value");
            return;
        }
        if (!minutesOfData)
        {
            alert("Invalid minutesOfData value");
            return;
        }

        compData.startDateTime = `${DateUtils.toNoTzDate(startDateTime)} ${DateUtils.prettyTime(startDateTime, false, true, false)}`
        compData.minutesOfData = minutesOfData;

        const results = await ChoiceApi.performance.getApiPerformanceResults(startDateTime, minutesOfData, props.resultsUserId);

        // Add a unique key to the data set
        compData.apiPerformanceResults = addUniqueKeyId(results);

        // Indicate we've loaded
        emit("newDataLoaded");
    }

    async function getTelemetryCustom()
    {
        if (!compData.startDateTime)
        {
            alert("Invalid telemetry.startDateTime value");
            return;
        }
        if (!compData.minutesOfData)
        {
            alert("Invalid telemetry.minutesOfData value");
            return;
        }

        return fetchTelemetryData(new Date(compData.startDateTime), compData.minutesOfData);
    }

    async function getTelemetry(dataRange: StandardTelemetryDataRange)
    {
        compData.showCustomDataRangeInputs = false;

        switch (dataRange)
        {
            case StandardTelemetryDataRange.Last15Minutes:
                await fetchTelemetryData(DateUtils.addMinutes(new Date(), -15), 15);
                break;

            case StandardTelemetryDataRange.LastHour:
                await fetchTelemetryData(DateUtils.addMinutes(new Date(), -60), 60);
                break;

            case StandardTelemetryDataRange.Today:
                {
                    const now = new Date();
                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    await fetchTelemetryData(today, 1440);
                }
                break;

            case StandardTelemetryDataRange.Yesterday:
                {
                    const now = new Date();
                    const yesterday = DateUtils.addDay(new Date(now.getFullYear(), now.getMonth(), now.getDate()), -1);
                    await fetchTelemetryData(yesterday, 1440);
                }
                break;            

            default:
                alert("Unexpected dataRange value");
            break;
        }
    }

    enum StandardTelemetryDataRange
    {
        Last15Minutes,
        LastHour,
        Today,
        Yesterday
    }

    const telemetryStartDateTime = computed<Date|undefined>(() => {
        if (!compData.startDateTime)
            return;
        return new Date(compData.startDateTime);
    });

    const telemetryEndDateTime = computed<Date|undefined>(() => {
        if (!telemetryStartDateTime.value)
            return;
        return DateUtils.addMinutes(telemetryStartDateTime.value, compData.minutesOfData);
    });

    function setCustomDataRange()
    {
        if (!compData.startDateTime)
        {
            const startTime = DateUtils.addSeconds(new Date(), -60 * compData.minutesOfData);
            compData.startDateTime = `${DateUtils.getNoTzToday()} ${DateUtils.prettyTime(startTime, false, true, false)}`
        }

        compData.showCustomDataRangeInputs = true;
    }

    function clearData()
    {
        compData.apiPerformanceResults = undefined;
    }


    const tableColumns = computed<ICol[]>(() => {
        const columns = [...degradedApiColumns];

        if (compData.showBody)
            columns.push({Name: "Body", DataField: "body"});

        if (compData.showCallHandler)
            columns.push({Name: "Handler", DataField: "handler"});

        if (compData.showScreenName)
            columns.push({Name: "Screen Name", DataField: "screenName"});
        if (compData.showScreenMethod)
            columns.push({Name: "Screen Method", DataField: "screenMethod" });

        return columns;
    });

    const tableData = computed(() =>{
        if (!compData.apiPerformanceResults)
            return;
        const dataItems = [...compData.apiPerformanceResults];

        // Parse screen_method out of XML into data
        if (compData.showScreenMethod)
        {
            const regExGetVal = /<screen_method>(?<val>.*)<\/screen_method>/gm;

            dataItems.forEach(item => { 
                if (item.body)
                {
                    const result = regExGetVal.exec(item.body);
                    if (result?.groups)
                    {
                        (<any>item).screenMethod = result.groups["val"];
                    }
                }
            });
        }

        // Parse screen_name out of XML into data
        if (compData.showScreenName)
        {
            const regExGetVal = /<screen_name>(?<val>.*)<\/screen_name>/gm;

            dataItems.forEach(item => { 
                if (item.body)
                {
                    const result = regExGetVal.exec(item.body);
                    if (result?.groups)
                    {
                        (<any>item).screenName = result.groups["val"];
                    }
                }
            });
        }

        return dataItems;
    });

    const showScreenMethodCheckbox = computed(() => {
        return compData.apiPerformanceResults?.find(item => 
                item.body 
                && 
                (
                    item.body.indexOf("<screen_method>") > -1 
                    &&
                    item.body.indexOf("</screen_method>") > -1 
                ));
    });

    const showScreenNameCheckbox = computed(() => {
        return compData.apiPerformanceResults?.find(item => 
                item.body 
                && 
                (
                    item.body.indexOf("<screen_name>") > -1 
                    &&
                    item.body.indexOf("</screen_name>") > -1 
                ));
    });    
</script>

<template>
    <div v-if="compData.isBangUser" class="mt-2">
        <SectionPanel>
            <div>
                <ActionButtonV2 :action="ActionButtonStyle.NoIcon" :performAction="async () => await getTelemetry(StandardTelemetryDataRange.Last15Minutes)" :endingPadding="1">Last 15 minutes</ActionButtonV2>
                <ActionButtonV2 :action="ActionButtonStyle.NoIcon" :performAction="async () => await getTelemetry(StandardTelemetryDataRange.LastHour)" :endingPadding="1">Last Hour</ActionButtonV2>
                <ActionButtonV2 :action="ActionButtonStyle.NoIcon" :performAction="async () => await getTelemetry(StandardTelemetryDataRange.Today)" :endingPadding="1">Today</ActionButtonV2>
                <ActionButtonV2 :action="ActionButtonStyle.NoIcon" :performAction="async () => await getTelemetry(StandardTelemetryDataRange.Yesterday)" :endingPadding="1">Yesterday</ActionButtonV2>
                <ChoiceButton :action="ActionButtonStyle.NoIcon" @click="setCustomDataRange">Custom</ChoiceButton>
            </div>
            <div class="mb-1" v-if="compData.startDateTime">
                <MessageText :displayStyle="TextDisplayStyle.Muted">
                    Last Fetched Data Range: <b>{{ DateUtils.prettyDateTime(telemetryStartDateTime) }}</b> to <b>{{ DateUtils.prettyDateTime(telemetryEndDateTime) }}</b>
                </MessageText>
            </div>
            <div v-if="compData.showCustomDataRangeInputs">
                <div>
                    Start Time: <DateTimePickerNoTZ v-model="compData.startDateTime" />
                </div>
                <div>
                    Minutes: <IntegerInput v-model:numberValue="compData.minutesOfData" :startingRange="1" :endingRange="1440" />
                </div>
                <ActionButtonV2 :action="ActionButtonStyle.NoIcon" :performAction="getTelemetryCustom">Get Custom</ActionButtonV2>
            </div>
        </SectionPanel>
    </div>


    <!-- Telemetry Panels -->
    <div v-if="undefined != compData.apiPerformanceResults">
        <MessageBadge :displayStyle="DisplayStyle.Danger">
            Currently, results do not provide full application coverage.
        </MessageBadge>

        <FoldingPanel>
            <template #title>Degraded API Calls</template>
            <template #body>
                <div class="row">
                    <div class="text-end">
                        <Checkbox v-model="compData.showBody" title="Show message body">Body</Checkbox>
                        <Checkbox v-model="compData.showCallHandler" title="Show what handled the call">Call Handler</Checkbox>
                        <Checkbox v-if="showScreenNameCheckbox" v-model="compData.showScreenName" title="Decode screen_name from Body for legacy SOAP calls">screen_name</Checkbox>
                        <Checkbox v-if="showScreenMethodCheckbox" v-model="compData.showScreenMethod" title="Decode screen_method from Body for legacy SOAP calls">screen_method</Checkbox>
                    </div>
                </div>

                <ChoiceTable
                    :columns="tableColumns" 
                    :data="tableData"
                    :allowSorting="true"
                    :showFilters="true"
                    keyValueDataMember="resultId" />
            </template>
        </FoldingPanel>
    </div>
    
</template>